
import { useState, useCallback } from 'react';
import { FunnelStage } from '../../../types';
import { ErrorHandler } from '../../../core/errors/ErrorHandler';
import {
    createFunnelStageUseCase,
    updateFunnelStageUseCase,
    deleteFunnelStageUseCase,
    getFunnelStagesUseCase
} from '../../../infrastructure/di/crmContainer';

export const useStageManagement = (selectedFunnelId: string | null, organizationId?: string) => {
    const [funnelStages, setFunnelStages] = useState<FunnelStage[]>([]);

    const refreshStages = useCallback(async (funnelIdOverride?: string) => {
        try {
            const idToUse = funnelIdOverride !== undefined ? funnelIdOverride : selectedFunnelId;
            const data = await getFunnelStagesUseCase.execute(idToUse || undefined);
            setFunnelStages(data);
        } catch (error) {
            ErrorHandler.handle(error as Error, 'RefreshStages');
        }
    }, [selectedFunnelId]);

    const saveFunnelStage = useCallback(async (stageToSave: FunnelStage) => {
        try {
            const isNew = !stageToSave.id || stageToSave.id.startsWith('temp_');
            // Use explicit funnelId from stageToSave if present, otherwise fallback to selectedFunnelId
            const funnelId = stageToSave.funnelId ?? selectedFunnelId;

            if (isNew) {
                await createFunnelStageUseCase.execute({
                    name: stageToSave.name,
                    conversionEvent: stageToSave.conversionEvent,
                    funnelId: funnelId,
                    order: stageToSave.order,
                    deadline: stageToSave.deadline,
                    color: stageToSave.color || null,
                    organizationId
                });
            } else {
                await updateFunnelStageUseCase.execute({
                    id: stageToSave.id,
                    name: stageToSave.name,
                    conversionEvent: stageToSave.conversionEvent,
                    order: stageToSave.order,
                    deadline: stageToSave.deadline,
                    color: stageToSave.color || null
                });
            }

            await refreshStages(funnelId || undefined);
        } catch (error) {
            ErrorHandler.handle(error as Error, 'SaveFunnelStage');
        }
    }, [selectedFunnelId, refreshStages, organizationId]);

    const reorderStages = useCallback(async (newOrderIds: string[]) => {
        // Atualizar estado local imediatamente (otimista)
        const newStages = [...funnelStages].sort((a, b) =>
            newOrderIds.indexOf(a.id) - newOrderIds.indexOf(b.id)
        );
        setFunnelStages(newStages);

        // Persistir no banco
        try {
            await Promise.all(
                newOrderIds.map((id, index) =>
                    updateFunnelStageUseCase.execute({ id, order: index })
                )
            );
        } catch (error) {
            ErrorHandler.handle(error as Error, 'ReorderStages');
            await refreshStages();
        }
    }, [funnelStages, refreshStages]);

    const deleteFunnelStage = useCallback(async (stageId: string) => {
        try {
            await deleteFunnelStageUseCase.execute(stageId);
            setFunnelStages(prev => prev.filter(s => s.id !== stageId));
        } catch (error) {
            ErrorHandler.handle(error as Error, 'DeleteFunnelStage');
        }
    }, []);

    return {
        funnelStages,
        setFunnelStages,
        refreshStages,
        saveFunnelStage,
        reorderStages,
        deleteFunnelStage
    };
};
